'use client'

import { ImportDataModal } from '@/components/import/ImportDataModal'
import DateRangeFilter from '@/components/date-range-filter'
import ListSortSelect, { AppointmentSort, AppointmentSortOrder } from '@/components/list-sort-select'
import { ClientSelector } from '@/components/client/ClientSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { UserSelector } from '@/components/UserSelector'
import {
    Exam,
    EXAM_STATUS_LABELS,
    ExamStatus,
    getExamsByCompany,
    getExamStatusColor,
    getExamStatusLabel,
} from '@/lib/api/exams/exam-api'
import { ShareableModule } from '@/lib/api/share/share-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import {
    FilterField,
    ListEmptyState,
    ListFilterBar,
    ListPageHeader,
} from '@/components/list-page'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAccessTime,
    MdAdd,
    MdArrowForward,
    MdBiotech,
    MdBusiness,
    MdFileDownload,
    MdHealthAndSafety,
    MdPerson,
    MdPointOfSale,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { BillingStatusFilter } from '@/components/list-page/billing-status-filter'

export default function ExamsPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const { canCreate } = useResourcePermissions('bookings', 'exams')
    const { ClientLabel } = useClientLabel()
    const [searchInput, setSearchInput] = useState('')
    const [filterById, setFilterById] = useState('')
    const [clientFilter, setClientFilter] = useState<number | null>(null)
    const [showImportModal, setShowImportModal] = useState(false)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    /** Vendedor do atendimento — é o `createdById` (o mesmo que a transferência de vendedor move). */
    const [sellerFilter, setSellerFilter] = useState<number | null>(null)
    /** Vazio = todos. Inclui "Cancelado" — exame cancelado não é excluído, fica listado. */
    const [statusFilter, setStatusFilter] = useState<ExamStatus | ''>('')
    /** Status da cobrança vinculada. Vazio = todos; `NONE` = sem cobrança. */
    const [billingStatusFilter, setBillingStatusFilter] = useState('')
    const [dateRange, setDateRange] = useState<{
        startDate?: string
        endDate?: string
    }>({})
    // Criação como padrão: o operador abre a lista para achar o que acabou de
    // registrar, não o que está marcado para a semana que vem.
    const [sortBy, setSortBy] = useState<AppointmentSort>('created')
    // Direção separada do critério: o operador escolhe entre começar pelo mais
    // recente (padrão) ou pelo mais antigo.
    const [sortOrder, setSortOrder] = useState<AppointmentSortOrder>('desc')

    const debouncedSearch = useDebounce(searchInput, 300)
    const debouncedId = useDebounce(filterById, 300)

    const filters = useMemo(
        () => ({
            search: debouncedSearch || undefined,
            status: statusFilter || undefined,
            partnerId: partnerFilter || undefined,
            id: debouncedId ? Number(debouncedId) : undefined,
            clientId: clientFilter || undefined,
            createdById: sellerFilter || undefined,
            billingStatus: billingStatusFilter || undefined,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            sortBy,
            sortOrder,
            // Exames nascidos de pacote são listados em /medical/packages.
            packages: 'exclude' as const,
        }),
        [
            debouncedSearch,
            statusFilter,
            partnerFilter,
            debouncedId,
            clientFilter,
            sellerFilter,
            billingStatusFilter,
            dateRange,
            sortBy,
            sortOrder,
        ]
    )

    const fetchFn = useCallback(
        (params: any) => {
            return getExamsByCompany(user!.companyId!, params)
        },
        [user?.companyId]
    )

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage, reload } =
        usePaginatedPage<Exam, typeof filters>({
            fetchFn,
            filters,
            limit: 15,
            enabled: !!user?.companyId,
            page: urlPage,
            onPageChange: setUrlPage,
        })

    const getExamDisplayName = (exam: Exam): string => {
        // Pacote: exibe o nome comercial do pacote (title), não os componentes.
        if (exam.packageCatalogId != null) {
            return exam.title
        }
        if (exam.items && exam.items.length > 0) {
            const first = exam.items[0].catalog?.name || 'Exame sem nome'
            const rest = exam.items.length - 1
            return rest > 0 ? `${first} +${rest}` : first
        }
        return exam.title
    }

    /** Quantos filtros o operador aplicou — o botão de limpar depende disso. */
    const activeFilters = [
        searchInput,
        filterById,
        clientFilter,
        partnerFilter,
        sellerFilter,
        statusFilter,
        billingStatusFilter,
        dateRange.startDate,
    ].filter(Boolean).length

    const clearFilters = () => {
        setSearchInput('')
        setFilterById('')
        setClientFilter(null)
        setPartnerFilter(null)
        setSellerFilter(null)
        setStatusFilter('')
        setBillingStatusFilter('')
        setDateRange({})
    }

    if (loading && items.length === 0) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando exames...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <ListPageHeader
                icon={<MdBiotech className="text-2xl" />}
                title="Exames"
                subtitle={`${total} ${total === 1 ? 'exame' : 'exames'}`}
                search={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Buscar exames...',
                }}
                actions={
                    <>
                        {/* Só ícone: é ação de apoio e o cabeçalho já está
                            cheio. Leva ao fechamento de caixa do próprio
                            operador (consultas + exames + procedimentos). */}
                        <button
                            onClick={() => navigate('/bookings/cash-closing')}
                            className="btn btn-sm btn-ghost btn-circle"
                            title="Fechamento de caixa"
                            aria-label="Fechamento de caixa"
                        >
                            <MdPointOfSale className="text-lg" />
                        </button>
                        {canCreate && (
                            <>
                                <button
                                    className="btn btn-sm btn-outline btn-secondary gap-2"
                                    onClick={() => setShowImportModal(true)}
                                >
                                    <MdFileDownload className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Importar
                                    </span>
                                </button>
                                <button
                                    onClick={() =>
                                        navigate(
                                            '/atendimento/scheduling?type=EXAM&status=BUDGET'
                                        )
                                    }
                                    className="btn btn-sm btn-accent btn-outline gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Orçamento
                                    </span>
                                </button>
                                <button
                                    onClick={() =>
                                        navigate(
                                            '/atendimento/scheduling?type=EXAM'
                                        )
                                    }
                                    className="btn btn-sm btn-secondary gap-2"
                                >
                                    <MdAdd className="text-lg" />
                                    <span className="hidden sm:inline">
                                        Exame
                                    </span>
                                </button>
                            </>
                        )}
                    </>
                }
            />

            <ListFilterBar activeCount={activeFilters} onClear={clearFilters}>
                <FilterField label="Número" htmlFor="exam-id-filter">
                    <input
                        id="exam-id-filter"
                        type="number"
                        placeholder="0"
                        value={filterById}
                        onChange={e => setFilterById(e.target.value)}
                        className="input input-bordered input-sm w-24"
                        min={1}
                    />
                </FilterField>
                <FilterField label={ClientLabel} className="w-56">
                    <ClientSelector
                        value={clientFilter}
                        onChange={id => setClientFilter(id)}
                        label=""
                        placeholder="Buscar por nome"
                        size="sm"
                    />
                </FilterField>
                <FilterField label="Status" htmlFor="exam-status-filter">
                    <select
                        id="exam-status-filter"
                        value={statusFilter}
                        onChange={e =>
                            setStatusFilter(e.target.value as ExamStatus | '')
                        }
                        className="select select-bordered select-sm w-40"
                    >
                        <option value="">Todos</option>
                        {(Object.keys(EXAM_STATUS_LABELS) as ExamStatus[]).map(
                            status => (
                                <option key={status} value={status}>
                                    {EXAM_STATUS_LABELS[status]}
                                </option>
                            )
                        )}
                    </select>
                </FilterField>
                {!hidePartnerSelector && (
                    <PartnerSelector
                        value={partnerFilter}
                        onChange={id => setPartnerFilter(id)}
                        label="Parceiro"
                        placeholder="Todos"
                        className="!w-48"
                        size="sm"
                    />
                )}
                {!hidePartnerSelector && (
                    <UserSelector
                        value={sellerFilter}
                        onChange={id => setSellerFilter(id)}
                        label="Vendedor"
                        placeholder="Todos"
                        className="!w-48"
                        size="sm"
                    />
                )}
                <BillingStatusFilter
                    value={billingStatusFilter}
                    onChange={setBillingStatusFilter}
                />
                <DateRangeFilter onChange={setDateRange} />
                <ListSortSelect
                    value={sortBy}
                    onChange={setSortBy}
                    order={sortOrder}
                    onOrderChange={setSortOrder}
                />
            </ListFilterBar>

            {items.length === 0 && !loading ? (
                <ListEmptyState
                    icon={<MdBiotech />}
                    title="Nenhum exame encontrado"
                    description="Comece adicionando seu primeiro exame."
                    filtered={activeFilters > 0}
                    onClearFilters={clearFilters}
                    action={
                        canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/atendimento/scheduling?type=EXAM')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-lg" />
                                Novo exame
                            </button>
                        )
                    }
                />
            ) : (
                <>
                    {/* A lista fica meio-apagada enquanto a página seguinte
                        carrega: sem isso, filtrar não dava sinal nenhum de que
                        algo estava acontecendo. */}
                    <div
                        className={`grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 xl:grid-cols-3 ${
                            loading ? 'pointer-events-none opacity-50' : ''
                        }`}
                    >
                        {items.map(exam => {
                            const displayName = getExamDisplayName(exam)
                            const itemCount = exam.items?.length || 0
                            const price =
                                exam.finalPrice || exam.totalPrice || 0

                            return (
                                <div
                                    key={exam.id}
                                    onClick={() =>
                                        navigate(`/bookings/exams/${exam.id}`)
                                    }
                                    className="group flex cursor-pointer flex-col rounded-2xl border border-base-300 bg-base-100 transition-colors hover:border-secondary/40 hover:bg-base-200/30"
                                >
                                    <div className="flex flex-col gap-3 p-5">
                                        <div className="flex items-start gap-3">
                                            <h3 className="line-clamp-2 min-w-0 flex-1 text-base font-semibold text-base-content">
                                                {displayName}
                                            </h3>
                                            {/* Sem text-white forçado: o badge do
                                                tema já traz o par de cores certo
                                                e o branco sumia nos tons claros. */}
                                            <span
                                                className={`badge ${getExamStatusColor(
                                                    exam.status,
                                                    exam.scheduledAt
                                                )} badge-sm shrink-0`}
                                            >
                                                {getExamStatusLabel(
                                                    exam.status,
                                                    exam.scheduledAt
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            {exam.client && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                    <MdPerson className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {exam.client.name}
                                                    </span>
                                                </div>
                                            )}
                                            {exam.partner && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                    <MdBusiness className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {exam.partner.name ||
                                                            exam.partner
                                                                .tradingName}
                                                    </span>
                                                </div>
                                            )}
                                            {exam.healthPlan && (
                                                <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                    <MdHealthAndSafety className="shrink-0 text-base text-base-content/40" />
                                                    <span className="truncate">
                                                        {exam.healthPlan.name}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {(exam.packageCatalogId != null ||
                                            itemCount > 0 ||
                                            (exam.discount != null &&
                                                exam.discount > 0) ||
                                            (exam.results &&
                                                exam.results.length > 0)) && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {exam.packageCatalogId !=
                                                null ? (
                                                    <span className="badge badge-sm badge-success badge-outline">
                                                        Pacote
                                                    </span>
                                                ) : (
                                                    itemCount > 0 && (
                                                        <span className="badge badge-sm badge-ghost gap-1">
                                                            <MdBiotech className="text-xs" />
                                                            {itemCount}{' '}
                                                            {itemCount === 1
                                                                ? 'exame'
                                                                : 'exames'}
                                                        </span>
                                                    )
                                                )}
                                                {exam.discount != null &&
                                                    exam.discount > 0 && (
                                                        <span className="badge badge-sm badge-accent badge-outline">
                                                            {exam.discount}%
                                                            desc.
                                                        </span>
                                                    )}
                                                {exam.results &&
                                                    exam.results.length > 0 && (
                                                        <span className="badge badge-sm badge-ghost">
                                                            {exam.results.length}{' '}
                                                            resultado
                                                            {exam.results
                                                                .length > 1
                                                                ? 's'
                                                                : ''}
                                                        </span>
                                                    )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-auto flex items-center justify-between gap-3 border-t border-base-300 px-5 py-3">
                                        <span className="flex items-center gap-1.5 text-xs text-base-content/50">
                                            <MdAccessTime className="text-sm" />
                                            {format(
                                                new Date(exam.requestedAt),
                                                'dd/MM/yyyy',
                                                { locale: ptBR }
                                            )}
                                        </span>
                                        <span className="flex items-center gap-2">
                                            {price > 0 && (
                                                <span className="text-sm font-bold text-secondary">
                                                    {(
                                                        price / 100
                                                    ).toLocaleString('pt-BR', {
                                                        style: 'currency',
                                                        currency: 'BRL',
                                                    })}
                                                </span>
                                            )}
                                            <MdArrowForward className="text-base text-base-content/30 transition-colors group-hover:text-secondary" />
                                        </span>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="exame"
                        />
                    )}
                </>
            )}
            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.EXAM}
                moduleLabel="Exames"
                hideFileImport
                fetchItems={async (companyId: number) => {
                    const response = await getExamsByCompany(companyId, {
                        packages: 'exclude',
                    })
                    return response.success ? response.result : []
                }}
                renderItem={(item: Exam) => (
                    <div>
                        <p className="truncate text-sm font-medium">
                            {getExamDisplayName(item)}
                        </p>
                        <p className="truncate text-xs text-base-content/60">
                            {item.client?.name} -{' '}
                            {getExamStatusLabel(item.status, item.scheduledAt)}
                        </p>
                        {item.results && item.results.length > 0 && (
                            <span className="badge badge-xs badge-ghost mt-1">
                                {item.results.length} resultado
                                {item.results.length > 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                )}
                getItemId={(item: Exam) => item.id}
                getItemName={(item: Exam) => getExamDisplayName(item)}
                onImportComplete={reload}
            />
        </div>
    )
}
